export const EXPERIMENTS = Object.freeze(['report', 'whatif']);

// Every arm can send messages for Stream A comparability and receive replies.
const BASE_ENDPOINTS = ['chat'];

// Feature to endpoint mapping is derived during buildArm so the arm table cannot
// render a feature while forgetting to authorize the endpoint it calls.
const FEATURE_ENDPOINTS = Object.freeze({
  // F1's Mirror screen also embeds F3's meter radar (oneSided), so it needs live meter data too.
  F1: ['mirror', 'meter'],
  F2: ['draft'],
  F3: ['meter'],
  F4: ['message-findings'],
});

function deriveEndpoints(features) {
  const extra = features.flatMap((feature) => FEATURE_ENDPOINTS[feature] ?? []);
  return [...new Set([...BASE_ENDPOINTS, ...extra])];
}

export function buildArm(name, arm) {
  if (!EXPERIMENTS.includes(arm.experiment)) {
    throw new Error(`[experiment-arms] arm "${name}" has unknown experiment "${arm.experiment}"`);
  }
  const features = [...(arm.features ?? [])];
  const isF2 = features.includes('F2');
  if (isF2 && (!arm.hintTiming || !arm.f2Variant)) {
    throw new Error(`[experiment-arms] F2 arm "${name}" must set hintTiming and f2Variant`);
  }
  if (!isF2 && (arm.hintTiming || arm.f2Variant)) {
    throw new Error(`[experiment-arms] non-F2 arm "${name}" must not set hintTiming/f2Variant`);
  }
  const config = {
    experiment: arm.experiment,
    features: Object.freeze(features),
    banner: Boolean(arm.banner),
    endpoints: Object.freeze(deriveEndpoints(features)),
  };
  if (isF2) {
    config.hintTiming = arm.hintTiming;
    config.f2Variant = arm.f2Variant;
  }
  return Object.freeze(config);
}

export const ARMS = Object.freeze({
  control: buildArm('control', { experiment: 'report', features: [] }),
  f1: buildArm('f1', { experiment: 'report', features: ['F1'] }),
  f3: buildArm('f3', { experiment: 'report', features: ['F3'] }),
  f4: buildArm('f4', { experiment: 'report', features: ['F4'] }),
  banner: buildArm('banner', { experiment: 'whatif', features: [], banner: true }),
  'f2-word-soft': buildArm('f2-word-soft', {
    experiment: 'whatif',
    features: ['F2'],
    hintTiming: 'word',
    f2Variant: 'soft',
  }),
  'f2-word-hard': buildArm('f2-word-hard', {
    experiment: 'whatif',
    features: ['F2'],
    hintTiming: 'word',
    f2Variant: 'hard',
  }),
  'f2-sentence-soft': buildArm('f2-sentence-soft', {
    experiment: 'whatif',
    features: ['F2'],
    hintTiming: 'sentence',
    f2Variant: 'soft',
  }),
  'f2-sentence-hard': buildArm('f2-sentence-hard', {
    experiment: 'whatif',
    features: ['F2'],
    hintTiming: 'sentence',
    f2Variant: 'hard',
  }),
});

export function isExperiment(value) {
  return EXPERIMENTS.includes(value);
}

export function isArm(value) {
  return Object.prototype.hasOwnProperty.call(ARMS, value);
}

export function armConfig(arm) {
  return ARMS[arm] ?? null;
}

export function armsForExperiment(experiment) {
  return Object.keys(ARMS).filter((arm) => ARMS[arm].experiment === experiment);
}
